import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import Count from '@risksmart-app/components/src/navigation/Count';
import type {
  Acceptance_Bool_Exp,
  Cause_Bool_Exp,
  Consequence_Bool_Exp,
  Control_Bool_Exp,
  Issue_Bool_Exp,
  Risk_Bool_Exp,
  Test_Result_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMessagesFeedStore } from 'src/components/messages/useMessagesFeedStore';

import {
  useGetAcceptanceCount,
  useGetAppetiteCount,
  useGetAssessmentActivityCount,
  useGetAssessmentCount,
  useGetAssessmentResultCount,
  useGetCauseCount,
  useGetConsequenceCount,
  useGetControlCount,
  useGetControlGroupCount,
  useGetImpactCount,
  useGetImpactRatingCount,
  useGetInternalAuditReportCount,
  useGetInternalAuditReportResultCount,
  useGetIssueCount,
  useGetOversightActivityCount,
  useGetOversightAssessmentCount,
  useGetOversightAssessmentResultCount,
  useGetRiskCount,
  useGetTestResultCount,
} from '@/hooks/queries';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

// NOTE (RSP-5727, corrected by COI-889): every badge count below is read through
// a `createQueryHook` count hook, and `createQueryHook` defaults
// `graphqlFetchPolicy` to `no-cache` — no count hook overrides it. An
// `evictFields` entry therefore cannot refresh a badge on either path; only
// `invalidateQueries` can. Each badge is also mounted for the whole session
// (`Navigation` sits outside the route outlet, and a collapsed section keeps its
// children mounted), so a missing invalidation is stale until a page reload.
// When an insert or delete hook moves one of these numbers, it MUST name the
// matching `*Count` query family — or its router-level `pathKey()` — in its
// `invalidate` array. Keep this list and those keys in sync.
export const CountOptions = [
  'issue',
  'issueRiskEvent',
  'issueBreachLog',
  'issueConsumerDuty',
  'issueCustomerTrust',
  'issueGDPRBreachLog',
  'issuePCIBreachLog',
  'issueSARLog',
  'risk',
  'acceptance',
  'appetite',
  'notification',
  'impact',
  'impactRating',
  'assessment',
  'internalAudit',
  'internalAuditReport',
  'assessmentResult',
  'assessmentActivity',
  'internalAuditReportResult',
  'oversightAssessment',
  'oversightAssessmentResult',
  'oversightActivity',
  'consequence',
  'cause',
  'request',
  'control',
  'controlGroup',
  'testResult',
] as const;

export type TCountOptions = (typeof CountOptions)[number];

const ConnectedCount: FC<{
  isActive?: boolean;
  badge?: boolean;
  countName: TCountOptions;
}> = ({ isActive, badge, countName }) => {
  const { entityIds } = useEntityFilter();

  const issueCountWhere = useEntityWhereFilter<Issue_Bool_Exp>(
    Parent_Type_Enum.Issue,
    {
      Type: {
        _eq: 'issue',
      },
    }
  );
  // The main 'issue' badge is entity-filtered; the subtype badges stay global
  // (no entityIds) to preserve existing behaviour.
  const { data: issueData } = useGetIssueCount({
    queryArgs: { issueType: 'issue', entityIds, where: issueCountWhere },
    shouldSkip: countName !== 'issue',
  });
  const { data: issueRiskEventData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_risk_event',
      where: { Type: { _eq: 'issue_risk_event' } },
    },
    shouldSkip: countName !== 'issueRiskEvent',
  });
  const { data: issueConsumerDutyData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_consumer_duty',
      where: { Type: { _eq: 'issue_consumer_duty' } },
    },
    shouldSkip: countName !== 'issueConsumerDuty',
  });
  const { data: issueCustomerTrustData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_customer_trust',
      where: { Type: { _eq: 'issue_customer_trust' } },
    },
    shouldSkip: countName !== 'issueCustomerTrust',
  });
  const { data: issueGDPRBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_gdpr_breach_log',
      where: { Type: { _eq: 'issue_gdpr_breach_log' } },
    },
    shouldSkip: countName !== 'issueGDPRBreachLog',
  });
  const { data: issuePCIBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_pci_breach_log',
      where: { Type: { _eq: 'issue_pci_breach_log' } },
    },
    shouldSkip: countName !== 'issuePCIBreachLog',
  });
  const { data: issueSARLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_sar_log',
      where: { Type: { _eq: 'issue_sar_log' } },
    },
    shouldSkip: countName !== 'issueSARLog',
  });
  const { data: issueBreachLogData } = useGetIssueCount({
    queryArgs: {
      issueType: 'issue_breach_log',
      where: { Type: { _eq: 'issue_breach_log' } },
    },
    shouldSkip: countName !== 'issueBreachLog',
  });

  const consequenceCountWhere = useEntityWhereFilter<Consequence_Bool_Exp>(
    Parent_Type_Enum.Consequence
  );
  const { data: consequenceData } = useGetConsequenceCount({
    queryArgs: { entityIds, where: consequenceCountWhere },
    shouldSkip: countName !== 'consequence',
  });

  const causeCountWhere = useEntityWhereFilter<Cause_Bool_Exp>(
    Parent_Type_Enum.Cause
  );
  const { data: causeData } = useGetCauseCount({
    queryArgs: { entityIds, where: causeCountWhere },
    shouldSkip: countName !== 'cause',
  });

  const { data: impactData } = useGetImpactCount({
    queryArgs: {},
    shouldSkip: countName !== 'impact',
  });
  const { data: impactRatingData } = useGetImpactRatingCount({
    queryArgs: {},
    shouldSkip: countName !== 'impactRating',
  });

  const riskCountWhere = useEntityWhereFilter<Risk_Bool_Exp>(
    Parent_Type_Enum.Risk
  );
  const { data: riskData } = useGetRiskCount({
    queryArgs: { entityIds, where: riskCountWhere },
    shouldSkip: countName !== 'risk',
  });

  // Appetite count is based on the risks
  const appetiteCountWhere = useEntityWhereFilter<Risk_Bool_Exp>(
    Parent_Type_Enum.Risk,
    { appetites_aggregate: { count: { predicate: { _gte: 1 } } } }
  );
  const { data: appetiteData } = useGetAppetiteCount({
    queryArgs: { entityIds, where: appetiteCountWhere },
    shouldSkip: countName !== 'appetite',
  });

  const acceptanceCountWhere = useEntityWhereFilter<Acceptance_Bool_Exp>(
    Parent_Type_Enum.Acceptance
  );
  const { data: acceptanceData } = useGetAcceptanceCount({
    queryArgs: { entityIds, where: acceptanceCountWhere },
    shouldSkip: countName !== 'acceptance',
  });
  const { data: assessmentData } = useGetAssessmentCount({
    queryArgs: {},
    shouldSkip: countName !== 'assessment',
  });
  const { data: internalAuditReportData } = useGetInternalAuditReportCount({
    queryArgs: {},
    shouldSkip: countName !== 'internalAuditReport',
  });
  const { data: oversightAssessmentData } = useGetOversightAssessmentCount({
    queryArgs: {},
    shouldSkip: countName !== 'oversightAssessment',
  });
  const { data: assessmentResultData } = useGetAssessmentResultCount({
    queryArgs: {},
    shouldSkip: countName !== 'assessmentResult',
  });

  const { data: assessmentActivityData } = useGetAssessmentActivityCount({
    queryArgs: {},
    shouldSkip: countName !== 'assessmentActivity',
  });

  const { data: oversightActivityData } = useGetOversightActivityCount({
    queryArgs: {},
    shouldSkip: countName !== 'oversightActivity',
  });

  const { data: internalAuditReportResultData } =
    useGetInternalAuditReportResultCount({
      queryArgs: {},
      shouldSkip: countName !== 'internalAuditReportResult',
    });

  const { data: oversightAssessmentResultData } =
    useGetOversightAssessmentResultCount({
      queryArgs: {},
      shouldSkip: countName !== 'oversightAssessmentResult',
    });

  const controlCountWhere = useEntityWhereFilter<Control_Bool_Exp>(
    Parent_Type_Enum.Control
  );
  const { data: controlData } = useGetControlCount({
    queryArgs: { entityIds, where: controlCountWhere },
    shouldSkip: countName !== 'control',
  });
  const { data: controlGroupData } = useGetControlGroupCount({
    queryArgs: {},
    shouldSkip: countName !== 'controlGroup',
  });

  const testResultCountWhere = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    { RatingType: { _in: ['assessment', 'rating'] } }
  );
  const { data: testResultData } = useGetTestResultCount({
    queryArgs: { entityIds, where: testResultCountWhere },
    shouldSkip: countName !== 'testResult',
  });
  const notificationsCount = useMessagesFeedStore(
    (state) => state.items.filter((i) => !i.read_at).length
  );

  let count: number | undefined = undefined;
  switch (countName) {
    case 'notification':
      count = notificationsCount;
      break;
    case 'issue':
      count = issueData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueBreachLog':
      count = issueBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueConsumerDuty':
      count = issueConsumerDutyData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueCustomerTrust':
      count = issueCustomerTrustData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueGDPRBreachLog':
      count = issueGDPRBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issuePCIBreachLog':
      count = issuePCIBreachLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueSARLog':
      count = issueSARLogData?.issue_aggregate.aggregate?.count;
      break;
    case 'issueRiskEvent':
      count = issueRiskEventData?.issue_aggregate.aggregate?.count;
      break;
    case 'cause':
      count = causeData?.cause_aggregate.aggregate?.count;
      break;
    case 'consequence':
      count = consequenceData?.consequence_aggregate.aggregate?.count;
      break;
    case 'risk':
      count = riskData?.risk_aggregate.aggregate?.count;
      break;
    case 'acceptance':
      count = acceptanceData?.acceptance_aggregate?.aggregate?.count;
      break;
    case 'appetite':
      count = appetiteData?.risk_aggregate.aggregate?.count;
      break;
    case 'impact':
      count = impactData?.impact_aggregate.aggregate?.count;
      break;
    case 'impactRating':
      count = impactRatingData?.impact_rating_aggregate.aggregate?.count;
      break;
    case 'assessment':
      count = assessmentData?.assessment_aggregate.aggregate?.count;
      break;
    case 'internalAuditReport':
      count =
        internalAuditReportData?.internal_audit_report_aggregate.aggregate
          ?.count;
      break;
    case 'internalAuditReportResult':
      count =
        (internalAuditReportResultData?.document_internal_audit_result_aggregate
          .aggregate?.count ?? 0) +
        (internalAuditReportResultData
          ?.obligation_internal_audit_result_aggregate.aggregate?.count ?? 0) +
        (internalAuditReportResultData
          ?.risk_controlled_internal_audit_result_aggregate.aggregate?.count ??
          0) +
        (internalAuditReportResultData
          ?.risk_uncontrolled_internal_audit_result_aggregate.aggregate
          ?.count ?? 0);
      break;
    case 'oversightAssessment':
      count =
        oversightAssessmentData?.oversight_assessment_aggregate.aggregate
          ?.count;
      break;
    case 'oversightAssessmentResult':
      count =
        (oversightAssessmentResultData?.document_oversight_result_aggregate
          .aggregate?.count ?? 0) +
        (oversightAssessmentResultData?.obligation_oversight_result_aggregate
          .aggregate?.count ?? 0) +
        (oversightAssessmentResultData
          ?.risk_controlled_oversight_result_aggregate.aggregate?.count ?? 0) +
        (oversightAssessmentResultData
          ?.risk_uncontrolled_oversight_result_aggregate.aggregate?.count ?? 0);
      break;
    case 'control':
      count = controlData?.control_aggregate.aggregate?.count;
      break;
    case 'controlGroup':
      count = controlGroupData?.control_group_aggregate.aggregate?.count;
      break;
    case 'testResult':
      count = testResultData?.test_result_aggregate.aggregate?.count;
      break;
    case 'assessmentResult':
      count =
        (assessmentResultData?.document_assessment_result_aggregate.aggregate
          ?.count ?? 0) +
        (assessmentResultData?.obligation_assessment_result_aggregate.aggregate
          ?.count ?? 0) +
        (assessmentResultData?.risk_assessment_result_aggregate.aggregate
          ?.count ?? 0);
      break;
    case 'assessmentActivity':
      count =
        assessmentActivityData?.assessment_activity_aggregate.aggregate?.count;
      break;
    case 'oversightActivity':
      count =
        oversightActivityData?.assessment_activity_aggregate.aggregate?.count;
      break;
  }

  return <Count count={count} badge={badge} isActive={isActive} />;
};

export default ConnectedCount;
